import { useState, useRef, useCallback, useEffect } from 'react';

interface DataPoint {
  name: string;
  value: number;
}

interface MiniLineChartProps {
  data: DataPoint[];
  color?: string;
  animate?: boolean;
}

function monotoneCubicPath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) return `M${points[0].x},${points[0].y}L${points[1].x},${points[1].y}`;

  // Compute tangent slopes using Fritsch-Carlson monotone method
  const n = points.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const m: number[] = [];

  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1].x - points[i].x);
    dy.push(points[i + 1].y - points[i].y);
    m.push(dy[i] / dx[i]);
  }

  const tangents: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      tangents.push(0);
    } else {
      tangents.push((m[i - 1] + m[i]) / 2);
    }
  }
  tangents.push(m[n - 2]);

  // Build cubic bezier path
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const seg = dx[i] / 3;
    const cp1x = points[i].x + seg;
    const cp1y = points[i].y + tangents[i] * seg;
    const cp2x = points[i + 1].x - seg;
    const cp2y = points[i + 1].y - tangents[i + 1] * seg;
    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${points[i + 1].x},${points[i + 1].y}`;
  }
  return d;
}

// Ease-out cubic for smooth deceleration
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function lerpPoints(
  from: { x: number; y: number }[],
  to: { x: number; y: number }[],
  t: number,
): { x: number; y: number }[] {
  return from.map((p, i) => ({
    x: p.x + (to[i].x - p.x) * t,
    y: p.y + (to[i].y - p.y) * t,
  }));
}

export default function MiniLineChart({
  data,
  color = '#fa8585',
  animate = true,
}: MiniLineChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lineRef = useRef<SVGPathElement>(null);
  const rafRef = useRef(0);

  // Layout constants
  const paddingTop = 16;
  const paddingBottom = 28;
  const paddingX = 16;
  const chartW = 320 - paddingX * 2;
  const chartH = 128 - paddingTop - paddingBottom;
  const baselineY = paddingTop + chartH;

  const handleInteraction = useCallback(
    (clientX: number) => {
      const svg = svgRef.current;
      if (!svg || data.length < 2) return;
      const rect = svg.getBoundingClientRect();
      const x = clientX - rect.left;
      const chartWidth = rect.width - paddingX * 2;
      const step = chartWidth / (data.length - 1);
      const idx = Math.round((x - paddingX) / step);
      setActiveIndex(Math.max(0, Math.min(data.length - 1, idx)));
    },
    [data.length],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => handleInteraction(e.clientX),
    [handleInteraction],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => handleInteraction(e.touches[0].clientX),
    [handleInteraction],
  );

  const handleLeave = useCallback(() => setActiveIndex(null), []);

  const maxVal = Math.max(...data.map((d) => d.value), 1);

  const realPoints =
    data.length >= 2
      ? data.map((d, i) => ({
          x: paddingX + (i * chartW) / (data.length - 1),
          y: paddingTop + chartH - (d.value / maxVal) * chartH,
        }))
      : [];

  // Track displayed points for rendering + as animation source for next transition
  const [displayPoints, setDisplayPoints] = useState(realPoints);
  const displayRef = useRef(displayPoints);
  displayRef.current = displayPoints;

  const prevDataKey = useRef('');
  // Incrementing key to re-trigger line draw + area fade CSS animations on data change
  const [drawKey, setDrawKey] = useState(0);

  useEffect(() => {
    if (data.length < 2) return;

    const key = JSON.stringify(data);
    const isSameData = key === prevDataKey.current;
    prevDataKey.current = key;

    if (isSameData) return;

    if (animate) setDrawKey((k) => k + 1);

    const target = data.map((d, i) => ({
      x: paddingX + (i * chartW) / (data.length - 1),
      y: paddingTop + chartH - (d.value / maxVal) * chartH,
    }));

    if (!animate) {
      setDisplayPoints(target);
      return;
    }

    // Animate from current positions (or baseline if first render) to target.
    // When point count changes (e.g. 7→30 days), start from baseline.
    const prev = displayRef.current;
    const from =
      prev.length === target.length ? prev : target.map((p) => ({ x: p.x, y: baselineY }));

    const duration = 600;
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);

      const current = lerpPoints(from, target, eased);
      setDisplayPoints(current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, animate]);

  const linePath = displayPoints.length >= 2 ? monotoneCubicPath(displayPoints) : '';
  const areaPath =
    displayPoints.length >= 2
      ? `${linePath}L${displayPoints[displayPoints.length - 1].x},${baselineY}L${displayPoints[0].x},${baselineY}Z`
      : '';

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 320 128"
      className="w-full h-full"
      preserveAspectRatio="none"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleLeave}
    >
      {/* Gradient fill under the line */}
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0.03} />
        </linearGradient>
      </defs>

      {data.length >= 2 && (
        <>
          {/* Area fill — fades in alongside the line draw */}
          <path
            key={`area-${drawKey}`}
            d={areaPath}
            fill="url(#lineGrad)"
            style={animate ? { animation: 'areaFadeIn 1s ease-out forwards', opacity: 0 } : {}}
          />

          {/* Line — left-to-right draw via stroke-dasharray, re-triggered by drawKey */}
          <path
            key={drawKey}
            ref={lineRef}
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth={3.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            {...(animate
              ? {
                  strokeDasharray: 600,
                  strokeDashoffset: 600,
                  style: { animation: 'lineDraw 1s ease-out forwards' },
                }
              : {})}
          />

          {/* X-axis labels */}
          {data.map((d, i) => (
            <text
              key={d.name}
              x={realPoints[i].x}
              y={128 - 4}
              textAnchor="middle"
              fontSize={9}
              fontWeight={700}
              fill="#64748b"
            >
              {d.name}
            </text>
          ))}

          {/* Active dot + tooltip */}
          {activeIndex !== null && (
            <>
              {/* Vertical cursor line */}
              <line
                x1={realPoints[activeIndex].x}
                y1={paddingTop}
                x2={realPoints[activeIndex].x}
                y2={baselineY}
                stroke="rgba(255,255,255,0.1)"
                strokeWidth={2}
              />
              {/* Dot */}
              <circle
                cx={realPoints[activeIndex].x}
                cy={realPoints[activeIndex].y}
                r={6}
                fill={color}
                stroke="#fff"
                strokeWidth={2}
              />
              {/* Tooltip */}
              <rect
                x={Math.min(Math.max(realPoints[activeIndex].x - 24, 4), 292)}
                y={Math.max(realPoints[activeIndex].y - 32, 2)}
                width={48}
                height={22}
                rx={8}
                fill="#1e293b"
              />
              <text
                x={Math.min(Math.max(realPoints[activeIndex].x, 28), 316)}
                y={Math.max(realPoints[activeIndex].y - 17, 17)}
                textAnchor="middle"
                fontSize={11}
                fontWeight={700}
                fill={color}
              >
                {data[activeIndex].value}
              </text>
            </>
          )}
        </>
      )}

      <style>{`
        @keyframes lineDraw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes areaFadeIn {
          to { opacity: 1; }
        }
      `}</style>
    </svg>
  );
}
